import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Input, Select, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import type { MonitoringOverview } from '../Monitoring/types';
import type { AnomalyEvent, AnomalyOverview } from '../Monitoring/anomalies/types';
import {
  buildFleetDevices,
  countUnreviewedAnomalies,
  filterFleetDevices,
  fleetIncidentCount,
  sensorTypeLabel,
  supportsAnomalies,
  supportsMonitoring,
  type AdminSensorOverviewItem,
  type FleetDevice,
} from './model';
import { exportFleetCsv, formatDate, HealthBadge, statusLabels } from './presentation';
import './overview.css';
import { HeartbeatBadge } from '../Monitoring/Heartbeat/DeviceHeartbeatPanel';
import AlertNavigation from '../Monitoring/AlertNavigation';
import type { UnifiedDevice } from '../Monitoring/Heartbeat/useDeviceHeartbeats';
import {
  heartbeatPresentation,
  heartbeatTime,
  modeLabels,
  type ModeFilter,
  type HeartbeatStatus,
} from '../Monitoring/Heartbeat/presentation';

export type SourceState = { loading: boolean; error: string | null };
const setupLabels = {
  legacy: 'Eksisterende alarmopsætning',
  prepared: 'Opsætning klar til aktivering',
  active: 'Samlet alarmstyring',
  attention: 'Opsætning kræver gennemgang',
  unknown: 'Alarmopsætning ikke bekræftet',
};
export type FleetOverviewProps = {
  sensors: AdminSensorOverviewItem[];
  monitoring?: MonitoringOverview;
  anomalies: AnomalyEvent[];
  scan?: AnomalyOverview['scan'];
  inventoryState: SourceState;
  monitoringState: SourceState;
  anomalyState: SourceState;
  hasMoreAnomalies: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  onLoadMore: () => void;
  onSelect: (device: FleetDevice) => void;
  onConfigure?: (device: FleetDevice) => void;
  initialOrgId?: string;
  heartbeats?: UnifiedDevice[];
  heartbeatState?: SourceState;
};

export default function FleetOverview(props: FleetOverviewProps) {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [orgId, setOrgId] = useState(props.initialOrgId || 'all');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive' | 'unknown'>('all');
  const [attention, setAttention] = useState<'all' | 'alarms' | 'anomalies' | 'unmonitored'>('all');
  const [mode, setMode] = useState<ModeFilter>('live');
  const [heartbeatStatus, setHeartbeatStatus] = useState<HeartbeatStatus | 'all'>('all');
  const [setupStatus, setSetupStatus] = useState<'all' | keyof typeof setupLabels>('all');
  const extraFilterCount = [status, heartbeatStatus].filter(
    (value) => value !== 'all'
  ).length;
  const heartbeatUnavailable = Boolean(
    props.heartbeatState?.loading ||
      props.heartbeatState?.error ||
      (props.heartbeatState && !props.heartbeats)
  );
  const heartbeatEnabled = Boolean(props.heartbeatState);
  const devices = useMemo(
    () => buildFleetDevices(props.sensors, props.monitoring, props.anomalies, props.heartbeats),
    [props.sensors, props.monitoring, props.anomalies, props.heartbeats]
  );
  const scopedDevices = useMemo(
    () =>
      filterFleetDevices(devices, {
        search,
        type,
        orgId,
        status,
        mode: heartbeatEnabled && !heartbeatUnavailable ? mode : 'all',
      }),
    [devices, search, type, orgId, status, mode, heartbeatEnabled, heartbeatUnavailable]
  );
  // Deadlines may pass between responses even when React Query preserves the data object.
  const filtered = filterFleetDevices(scopedDevices, {
    attention,
    unified: heartbeatEnabled,
  }).filter(
    (device) =>
      (setupStatus === 'all' || (device.heartbeat?.alertSetup?.status ?? 'unknown') === setupStatus) &&
      (heartbeatStatus === 'all' ||
        (device.heartbeat &&
          heartbeatPresentation(device.heartbeat.heartbeat).status === heartbeatStatus))
  );
  const typeOptions = useMemo(
    () =>
      Array.from(new Set(devices.map((d) => d.type)))
        .sort((a, b) => sensorTypeLabel(a).localeCompare(sensorTypeLabel(b), 'da'))
        .map((value) => ({ value, label: sensorTypeLabel(value) })),
    [devices]
  );
  const orgOptions = useMemo(
    () =>
      Array.from(new Map(devices.map((d) => [d.orgId, d.orgName])).entries())
        .sort((a, b) => a[1].localeCompare(b[1], 'da'))
        .map(([value, label]) => ({ value, label })),
    [devices]
  );
  const alarms = scopedDevices.reduce(
    (sum, d) => sum + (fleetIncidentCount(d, heartbeatEnabled) || 0),
    0
  );
  const unreviewed = scopedDevices.reduce(
    (sum, d) => sum + countUnreviewedAnomalies(d.anomalies),
    0
  );
  const unmonitored = scopedDevices.filter((d) =>
    heartbeatEnabled ? d.heartbeat?.heartbeat.status === 'unsupported' : !supportsMonitoring(d.type)
  ).length;
  const overdue = scopedDevices.filter(
    (d) =>
      d.heartbeat &&
      d.heartbeat.heartbeat.ruleMode !== 'test' &&
      heartbeatPresentation(d.heartbeat.heartbeat).status === 'overdue'
  ).length;
  const missingMode = devices.filter((d) => !d.heartbeat).length;
  const monitoringUnavailable =
    props.monitoringState.loading || Boolean(props.monitoringState.error) || !props.monitoring;
  const hasLegacyDevices = scopedDevices.some(
    (device) => device.heartbeat?.alertSetup?.status !== 'active'
  );
  const alarmsUnavailable = heartbeatEnabled
    ? heartbeatUnavailable || (hasLegacyDevices && monitoringUnavailable)
    : monitoringUnavailable;
  const anomalyUnavailable = props.anomalyState.loading || Boolean(props.anomalyState.error);
  const inventoryUnavailable =
    props.inventoryState.loading || Boolean(props.inventoryState.error) || heartbeatUnavailable;
  const paused = props.scan?.control?.enabled === false;
  const monitoringOld = Boolean(
    props.monitoring &&
      (!props.monitoring.summary.lastEvaluatedAt ||
        Date.now() - props.monitoring.summary.lastEvaluatedAt.getTime() > 60 * 60 * 1000)
  );
  const anomaliesOld = Boolean(
    props.scan &&
      (!props.scan.lastCompletedAt ||
        Date.now() - props.scan.lastCompletedAt > 3 * 60 * 60 * 1000 ||
        (props.scan.lastFailedAt || 0) > props.scan.lastCompletedAt)
  );

  const columns: ColumnsType<FleetDevice> = [
    {
      title: 'Enhed / kunde',
      key: 'device',
      width: 265,
      render: (_, d) => (
        <div className="fleet-identity">
          <button className="fleet-device-link" onClick={() => props.onSelect(d)}>
            {d.sensorName}
          </button>
          <span>{d.orgName}</span>
          <small>{d.deviceId || d.sensorId}</small>
          {heartbeatEnabled && (
            <small>
              {d.heartbeat?.alertSetup
                ? setupLabels[d.heartbeat.alertSetup.status]
                : 'Alarmopsætning ikke bekræftet'}
            </small>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 165,
      render: (_, d) => (
        <div className="fleet-cell">
          <span>{sensorTypeLabel(d.type)}</span>
          <small>{statusLabels[d.status]}</small>
          {heartbeatEnabled && (
            <small>
              Driftsform:{' '}
              {d.heartbeat
                ? modeLabels[d.heartbeat.mode.mode]
                : heartbeatUnavailable
                ? 'afventer hentning'
                : 'ukendt'}
            </small>
          )}
        </div>
      ),
    },
    {
      title: 'Alarmovervågning',
      key: 'health',
      width: 200,
      render: (_, d) => (
        <div className="fleet-cell">
          <HealthBadge
            device={d}
            unavailable={Boolean(props.monitoringState.error)}
            loading={props.monitoringState.loading}
          />
          {d.monitoring?.lastDataAt && <small>Data: {formatDate(d.monitoring.lastDataAt)}</small>}
          {d.monitoring && !d.monitoring.lastDataAt && <small>Seneste data ukendt</small>}
        </div>
      ),
    },
    {
      title: 'Åbne alarmer',
      key: 'alarms',
      width: 140,
      render: (_, d) => {
        const count = fleetIncidentCount(d, heartbeatEnabled);
        const legacy = heartbeatEnabled && d.heartbeat?.alertSetup?.status !== 'active';
        const unavailable = heartbeatEnabled
          ? heartbeatUnavailable || (legacy && monitoringUnavailable)
          : monitoringUnavailable;
        return unavailable ? (
          <span className="fleet-muted">—</span>
        ) : count !== undefined ? (
          <div className="fleet-cell">
            <button
              className={`fleet-count ${count ? 'fleet-count-alert' : ''}`}
              onClick={() => props.onSelect(d)}
              aria-label={`${count} åbne alarmer for ${d.sensorName}`}
            >
              {count}
            </button>
            {legacy && <small>Eksisterende overvågning</small>}
          </div>
        ) : (
          <span className="fleet-muted">—</span>
        );
      },
    },
    {
      title: 'Afvigelser',
      key: 'anomalies',
      width: 200,
      render: (_, d) => {
        if (!supportsAnomalies(d.type)) return <span className="fleet-muted">Ikke analyseret</span>;
        if (anomalyUnavailable)
          return (
            <span className="fleet-muted">
              {props.anomalyState.loading ? 'Henter…' : 'Utilgængelig'}
            </span>
          );
        const count = countUnreviewedAnomalies(d.anomalies);
        return (
          <div className="fleet-cell">
            <button
              className={`fleet-inline ${count ? 'fleet-inline-review' : ''}`}
              onClick={() => props.onSelect(d)}
            >
              {count
                ? `${count}${props.hasMoreAnomalies ? '+' : ''} til opfølgning`
                : props.hasMoreAnomalies
                ? 'Ingen i det indlæste'
                : 'Ingen til opfølgning'}
            </button>
            <small>
              {d.anomalies.find((a) => a.review.status !== 'reviewed')?.title ||
                'Se enhedens historik'}
            </small>
          </div>
        );
      },
    },
    {
      title: 'Handling',
      key: 'open',
      width: 155,
      render: (_, d) => (
        <Button
          icon={<SettingOutlined />}
          aria-label={`Indstillinger for ${d.sensorName}`}
          onClick={() => (props.onConfigure || props.onSelect)(d)}
        >
          Indstillinger
        </Button>
      ),
    },
  ];
  if (heartbeatEnabled)
    columns.splice(
      columns.findIndex((column) => column.key === 'health'),
      1
    );
  if (heartbeatEnabled)
    columns.splice(2, 0, {
      title: 'Datakontrol',
      key: 'heartbeat',
      width: 240,
      render: (_, device) => {
        if (heartbeatUnavailable)
          return (
            <span className="fleet-muted">
              {props.heartbeatState?.loading ? 'Henter datakontrol…' : 'Datakontrol utilgængelig'}
            </span>
          );
        if (!device.heartbeat)
          return <span className="fleet-muted">Afventer første datakontrol</span>;
        const heartbeat = device.heartbeat.heartbeat;
        const display = heartbeatPresentation(heartbeat);
        return (
          <div className="fleet-cell">
            <HeartbeatBadge device={device.heartbeat} />
            <small>Faktiske data: {heartbeatTime(heartbeat.lastDataAt)}</small>
            <small>
              Frist:{' '}
              {heartbeat.expectedAfter === null
                ? 'ingen fast frist'
                : heartbeatTime(heartbeat.expectedAfter)}
              {heartbeat.ruleMode === 'test' ? ' · Test-regel' : ''}
            </small>
            <small>
              Kontrolleret: {heartbeatTime(heartbeat.evaluatedAt)}
              {display.evaluationStale ? ' · vurderingen er gammel' : ''}
            </small>
          </div>
        );
      },
    });
  function reset() {
    setSearch('');
    setType('all');
    setOrgId('all');
    setStatus('all');
    setAttention('all');
    setMode('live');
    setHeartbeatStatus('all');
    setSetupStatus('all');
  }
  return (
    <main className="fleet-overview">
      <div className="fleet-heading">
        <div>
          <div className="fleet-eyebrow">
            AKS INTERN <span> / </span> DRIFT
          </div>
          <h1>Alarmoverblik</h1>
          <p>
            Se status, alarmstyring og dækning på tværs af enheder. Åbn Indstillinger for den enkelte enhed.
          </p>
        </div>
        <div className="fleet-actions">
          <Button icon={<ReloadOutlined />} loading={props.refreshing} onClick={props.onRefresh}>
            Opdatér
          </Button>
        </div>
      </div>
      <AlertNavigation active="devices" />
      {props.inventoryState.error && (
        <Alert
          showIcon
          type="error"
          message="Enhedslisten kunne ikke opdateres"
          description={
            devices.length
              ? 'Viser den senest hentede liste. Prøv Opdatér igen.'
              : props.inventoryState.error
          }
        />
      )}
      {(!heartbeatEnabled || hasLegacyDevices) && props.monitoringState.error && (
        <Alert
          showIcon
          type="warning"
          message="Overvågning kunne ikke opdateres"
          description="Enhedslisten er stadig tilgængelig. Alarmtal og overvågningsstatus kan ikke bekræftes."
        />
      )}
      {props.anomalyState.error && (
        <Alert
          showIcon
          type="warning"
          message="Afvigelser kunne ikke opdateres"
          description="Enhedslisten er stadig tilgængelig. Prøv Opdatér for at hente vurderingerne igen."
        />
      )}
      {props.heartbeatState?.error && (
        <Alert
          showIcon
          type="warning"
          message="Datakontrol og driftsformer kunne ikke hentes"
          description="Viser alle registrerede enheder. Live-filteret og driftstallene kan først bekræftes, når datakontrollen er hentet."
        />
      )}
      {heartbeatEnabled && !heartbeatUnavailable && missingMode > 0 && (
        <Alert
          showIcon
          type="info"
          message={`${missingMode} enheder mangler en driftsform i den fælles overvågning`}
          description="De tælles ikke som Live. Vælg alle driftsformer for at se dem."
          action={
            <Button size="small" onClick={() => setMode('all')}>
              Vis alle
            </Button>
          }
        />
      )}
      {(((!heartbeatEnabled || hasLegacyDevices) && monitoringOld) || anomaliesOld || paused) && (
        <Alert
          showIcon
          type="warning"
          message={
            paused
              ? 'Afvigelsesanalysen er sat på pause'
              : 'Nogle vurderinger er ældre end forventet'
          }
          description="Se tidspunkterne nedenfor. Resultaterne viser den seneste vurdering og bekræfter ikke enhedens tilstand lige nu."
        />
      )}
      <div
        className={`fleet-stats${heartbeatEnabled ? ' fleet-stats-heartbeat' : ''}`}
        aria-label="Overblik over registrerede enheder i det valgte filter"
      >
        <button
          className={attention === 'all' ? 'fleet-stat is-selected' : 'fleet-stat'}
          onClick={() => setAttention('all')}
        >
          <span>Registrerede enheder</span>
          <strong>{inventoryUnavailable ? '—' : scopedDevices.length}</strong>
          <small>
            {heartbeatEnabled
              ? `${
                  mode === 'all' ? 'Alle driftsformer' : modeLabels[mode]
                } · valgte kunder og typer`
              : 'Alle kunder og enhedstyper'}
          </small>
        </button>
        {heartbeatEnabled && (
          <button
            className={heartbeatStatus === 'overdue' ? 'fleet-stat is-selected' : 'fleet-stat'}
            onClick={() => setHeartbeatStatus(heartbeatStatus === 'overdue' ? 'all' : 'overdue')}
          >
            <span>Forsinkede data</span>
            <strong>{inventoryUnavailable ? '—' : overdue}</strong>
            <small>Efter fristen i den gældende regel</small>
          </button>
        )}
        <button
          className={attention === 'alarms' ? 'fleet-stat is-selected' : 'fleet-stat'}
          onClick={() => setAttention('alarms')}
        >
          <span>
            <i className="fleet-dot fleet-dot-red" /> Åbne alarmer
          </span>
          <strong>{alarmsUnavailable || inventoryUnavailable ? '—' : alarms}</strong>
          <small>
            {heartbeatEnabled ? 'Hændelser og eksisterende overvågning' : 'Fra central overvågning'}
          </small>
        </button>
        <button
          className={attention === 'anomalies' ? 'fleet-stat is-selected' : 'fleet-stat'}
          onClick={() => setAttention('anomalies')}
        >
          <span>
            <i className="fleet-dot fleet-dot-blue" /> Afvigelser til opfølgning
          </span>
          <strong>
            {anomalyUnavailable || inventoryUnavailable
              ? '—'
              : `${unreviewed}${props.hasMoreAnomalies ? '+' : ''}`}
          </strong>
          <small>
            {props.hasMoreAnomalies
              ? 'Blandt de indlæste · flere kan findes'
              : 'Til vurdering eller under undersøgelse'}
          </small>
        </button>
        <button
          className={attention === 'unmonitored' ? 'fleet-stat is-selected' : 'fleet-stat'}
          onClick={() => setAttention('unmonitored')}
        >
          <span>{heartbeatEnabled ? 'Uden fast datakontrol' : 'Uden central overvågning'}</span>
          <strong>{inventoryUnavailable ? '—' : unmonitored}</strong>
          <small>
            {heartbeatEnabled
              ? 'Ingen understøttet heartbeat'
              : 'Enhedstyper med andre opsætninger'}
          </small>
        </button>
      </div>
      <div className="fleet-freshness">
        {heartbeatEnabled && (
          <span>
            Datakontrol viser faktiske data og regelbaseret frist. En ny kontrol er ikke en ny
            måling.
          </span>
        )}
        {(!heartbeatEnabled || hasLegacyDevices) && (
          <span>
            {heartbeatEnabled ? 'Eksisterende overvågning' : 'Overvågning'}:{' '}
            {props.monitoringState.loading
              ? 'henter…'
              : props.monitoringState.error
              ? 'utilgængelig'
              : formatDate(props.monitoring?.summary.lastEvaluatedAt)}
          </span>
        )}
        <span>
          Afvigelsesanalyse:{' '}
          {props.anomalyState.loading
            ? 'henter…'
            : props.anomalyState.error
            ? 'utilgængelig'
            : formatDate(props.scan?.lastCompletedAt)}
          {paused ? ' · sat på pause' : ''}
        </span>
        <Link to={heartbeatEnabled ? '/monitoring/unified/rules' : '/monitoring'}>
          {heartbeatEnabled ? 'Fælles regler' : 'Alarmoversigt'} <ArrowRightOutlined />
        </Link>
      </div>
      <section className="fleet-list" aria-label="Enheder">
        <div className="fleet-filters">
          <Input
            aria-label="Søg efter enhed"
            placeholder="Søg enhed, kunde eller ID…"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            allowClear
          />
          <Select
            aria-label="Kunde"
            value={orgId}
            onChange={setOrgId}
            showSearch
            optionFilterProp="label"
            options={[{ value: 'all', label: 'Alle kunder' }, ...orgOptions]}
          />
          <Select
            aria-label="Enhedstype"
            value={type}
            onChange={setType}
            options={[{ value: 'all', label: 'Alle typer' }, ...typeOptions]}
          />
          {heartbeatEnabled && (
            <Select
              aria-label="Driftsform"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'all', label: 'Alle driftsformer' },
                ...Object.entries(modeLabels).map(([value, label]) => ({ value, label })),
              ]}
            />
          )}
        </div>
        {heartbeatEnabled && (
          <section className="fleet-setup" aria-label="Alarmstyring og dækning">
            <h2>Hvem styrer alarmerne?</h2>
            <p>
              Antal enheder for den valgte kunde, type og driftsform. Vælg en opsætning for at
              se enhederne. Overførsel og aktivering sker under enhedens Indstillinger.
            </p>
            <div className="fleet-setup-options">
              <button type="button" aria-pressed={setupStatus === 'all'}
                disabled={inventoryUnavailable}
                onClick={() => { setSetupStatus('all'); setAttention('all'); setHeartbeatStatus('all'); }}>
                Alle opsætninger <strong>{inventoryUnavailable ? '—' : scopedDevices.length}</strong>
              </button>
              {Object.entries(setupLabels).map(([value, label]) => (
                <button type="button" key={value} aria-pressed={setupStatus === value}
                  disabled={inventoryUnavailable}
                  onClick={() => {
                    setSetupStatus(value as keyof typeof setupLabels);
                    setAttention('all');
                    setHeartbeatStatus('all');
                  }}>
                  {label} <strong>{inventoryUnavailable ? '—' : scopedDevices.filter(
                    device => (device.heartbeat?.alertSetup?.status ?? 'unknown') === value
                  ).length}</strong>
                </button>
              ))}
            </div>
            <details className="fleet-setup-other">
              <summary>Rapporter og varsler uden for hændelseslisten</summary>
              <p>
                Enhedens opsætning angiver ejerskab for de overførte enhedsalarmer.
                Følgende særskilte rapporter indgår ikke i antallet af åbne hændelser:
              </p>
              <ul>
                <li>Særlige FLOW-rapporter om niveau, påfyldning og dosering.</li>
                <li>Niveaustatus til udvalgte ansvarlige.</li>
                <li>Vejrrapporter om frost og regn.</li>
              </ul>
              <p>
                Denne liste beskriver de kendte rapporttyper; deres seneste kørsel og levering
                er ikke kontrolleret her. Afvigelsesanalysen har sin egen vurderingsliste og
                sender ingen driftsalarmer.
              </p>
              <Link to="/monitoring/legacy">Åbn ældre overvågning</Link>
            </details>
          </section>
        )}
        <details className="fleet-extra-filters">
          <summary>
            Flere filtre
            {extraFilterCount > 0
              ? ` (${extraFilterCount} ${extraFilterCount === 1 ? 'aktivt' : 'aktive'})`
              : ''}
          </summary>
          <div className="fleet-extra-filter-fields">
            <Select
              aria-label="Registreringsstatus"
              value={status}
              onChange={setStatus}
              options={[
                { value: 'all', label: 'Alle registreringer' },
                ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
              ]}
            />
            {heartbeatEnabled && (
              <Select
                aria-label="Datakontrolstatus"
                value={heartbeatStatus}
                onChange={setHeartbeatStatus}
                options={[
                  { value: 'all', label: 'Alle datastatusser' },
                  { value: 'on_time', label: 'Data til tiden' },
                  { value: 'overdue', label: 'Data forsinket' },
                  { value: 'never_seen', label: 'Ingen data registreret' },
                  { value: 'unknown', label: 'Datatidspunkt ukendt' },
                  { value: 'paused', label: 'Datakontrol pauset' },
                  { value: 'unsupported', label: 'Ingen fast datakontrol' },
                ]}
              />
            )}

          </div>
        </details>
        <div className="fleet-list-toolbar">
          <div className="fleet-view-tabs" aria-label="Vis enheder">
            {(
              [
                { value: 'all', label: 'Alle' },
                { value: 'alarms', label: 'Med alarmer' },
                { value: 'anomalies', label: 'Med afvigelser' },
                {
                  value: 'unmonitored',
                  label: heartbeatEnabled ? 'Uden fast datakontrol' : 'Uden overvågning',
                },
              ] as const
            ).map((item) => (
              <button
                key={item.value}
                aria-pressed={attention === item.value}
                onClick={() => setAttention(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="fleet-actions">
            <Button size="small" type="text" onClick={reset}>
              Nulstil filtre
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              disabled={!filtered.length}
              onClick={() => exportFleetCsv(filtered)}
            >
              Eksportér
            </Button>
          </div>
        </div>
        <Table<FleetDevice>
          rowKey="key"
          dataSource={filtered}
          columns={columns}
          loading={props.inventoryState.loading}
          pagination={{
            defaultPageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100],
            showTotal: (total) => `${total} enheder`,
          }}
          scroll={{ x: heartbeatEnabled ? 1280 : 1040 }}
          locale={{
            emptyText: props.inventoryState.error
              ? 'Enhedslisten er utilgængelig.'
              : attention === 'alarms' && alarmsUnavailable
              ? 'Alarmstatus er ikke tilgængelig endnu. Prøv Opdatér.'
              : attention === 'anomalies' && anomalyUnavailable
              ? 'Afvigelser er ikke tilgængelige endnu. Prøv Opdatér.'
              : attention === 'anomalies' && props.hasMoreAnomalies
              ? 'Ingen match blandt de indlæste afvigelser. Hent flere nedenfor.'
              : 'Ingen enheder matcher filtrene.',
          }}
        />
      </section>
      <div className="fleet-coverage">
        <div>
          <strong>Afvigelser supplerer alarmovervågningen</strong>
          <p>
            Analysen omfatter FLOW- og GSM-anlæg. En vurderet afvigelse lukker ikke en alarm.
            {props.hasMoreAnomalies &&
              ` Oversigten bygger på de ${props.anomalies.length} indlæste afvigelser. Åbn en enhed for dens egen historik.`}
          </p>
        </div>
        {props.hasMoreAnomalies && (
          <Button loading={props.loadingMore} onClick={props.onLoadMore}>
            Hent flere afvigelser
          </Button>
        )}
      </div>
    </main>
  );
}
